import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import Editor, { type Monaco, type OnMount } from '@monaco-editor/react';
import type { editor } from 'monaco-editor';
import type { CompletionCatalog, ValidateQueryResponse } from '@idas/shared';
import { applyValidationMarkers, defineThemes, registerSqlCompletions } from '../lib/monaco-sql';

export interface SqlEditorHandle {
  insertAtCursor: (text: string) => void;
  focus: () => void;
  /** The selected text, or the whole document when nothing is selected. */
  getSelectedOrAll: () => string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  onRun: () => void;
  catalog: CompletionCatalog | null;
  validation: ValidateQueryResponse | null;
  theme: 'dark' | 'light';
  readOnly?: boolean;
}

export const SqlEditor = forwardRef<SqlEditorHandle, Props>(function SqlEditor(
  { value, onChange, onRun, catalog, validation, theme, readOnly },
  ref,
) {
  const editorRef = useRef<editor.IStandaloneCodeEditor | null>(null);
  const monacoRef = useRef<Monaco | null>(null);

  // Held in a ref so the completion provider — registered once — always reads
  // the current catalog. Re-registering on every catalog change would leak
  // providers and make completions fire multiple times.
  const catalogRef = useRef<CompletionCatalog | null>(catalog);
  catalogRef.current = catalog;

  // Likewise for the run handler: the Ctrl+Enter command is bound once, and a
  // stale closure would run whatever SQL was in the tab when it was bound.
  const runRef = useRef(onRun);
  runRef.current = onRun;

  useImperativeHandle(ref, () => ({
    insertAtCursor(text) {
      const instance = editorRef.current;
      if (!instance) return;

      const selection = instance.getSelection();
      if (!selection) return;

      instance.executeEdits('insert-from-tree', [
        { range: selection, text, forceMoveMarkers: true },
      ]);
      instance.focus();
    },
    focus() {
      editorRef.current?.focus();
    },
    getSelectedOrAll() {
      const instance = editorRef.current;
      if (!instance) return value;

      const selection = instance.getSelection();
      const selected = selection ? instance.getModel()?.getValueInRange(selection) : '';
      // Running just the highlighted statement is the behaviour every SQL tool
      // has, and the one people reach for without thinking.
      return selected && selected.trim() ? selected : instance.getValue();
    },
  }));

  const handleMount: OnMount = (instance, monaco) => {
    editorRef.current = instance;
    monacoRef.current = monaco;

    defineThemes(monaco);
    monaco.editor.setTheme(theme === 'dark' ? 'idas-dark' : 'idas-light');

    registerSqlCompletions(monaco, () => catalogRef.current);

    instance.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Enter, () => runRef.current());
    // Shift+Enter as well: muscle memory differs between tools, and both are
    // unambiguous here since neither means anything else in an editor.
    instance.addCommand(monaco.KeyMod.Shift | monaco.KeyCode.Enter, () => runRef.current());
  };

  useEffect(() => {
    monacoRef.current?.editor.setTheme(theme === 'dark' ? 'idas-dark' : 'idas-light');
  }, [theme]);

  useEffect(() => {
    const monaco = monacoRef.current;
    const model = editorRef.current?.getModel();
    if (!monaco || !model) return;
    applyValidationMarkers(monaco, model, validation);
  }, [validation]);

  return (
    <Editor
      language="sql"
      value={value}
      onChange={(next) => onChange(next ?? '')}
      onMount={handleMount}
      loading={<div className="p-4 text-2xs text-ink-faint">Loading editor…</div>}
      options={{
        readOnly,
        fontSize: 13,
        fontFamily: "'JetBrains Mono', ui-monospace, Consolas, monospace",
        fontLigatures: true,
        lineHeight: 1.6,
        minimap: { enabled: false },
        scrollBeyondLastLine: false,
        automaticLayout: true,
        padding: { top: 12, bottom: 12 },
        renderLineHighlight: 'line',
        smoothScrolling: true,
        cursorBlinking: 'smooth',
        // Analysts write long queries; hard wrapping beats horizontal scroll.
        wordWrap: 'on',
        tabSize: 2,
        suggestSelection: 'first',
        // Suggest as you type rather than only on Ctrl+Space — schema
        // discovery is most of the value of the completion list.
        quickSuggestions: { other: true, comments: false, strings: false },
        suggest: { showWords: false, preview: true },
        scrollbar: { verticalScrollbarSize: 10, horizontalScrollbarSize: 10 },
        overviewRulerLanes: 0,
        bracketPairColorization: { enabled: true },
      }}
    />
  );
});
